/**
 * Отдельный vuex модуль для взаимодействия по analytics/parking_detection-API
 */

import {ANALYTICS, AnalyticSettings} from "@/store/analytics/helpers.js";
import makeMessage from "@/store/analytics/analyticMessage.js";

// actions для parking_detections. Вызов действия начинать с "analytics/parkingDetections/"
export const ACTION_LOAD_PARKING_DETECTIONS_SETTINGS = "LOAD_PARKING_DETECTIONS_SETTINGS";
export const ACTION_LOAD_PARKING_DETECTION_SETTINGS = "LOAD_PARKING_DETECTION_SETTINGS";
export const ACTION_LOAD_PARKING_DETECTION_EVENTS = "LOAD_PARKING_DETECTION_EVENTS";
export const ACTION_LOAD_PARKING_DETECTION_EVENT = "LOAD_PARKING_DETECTION_EVENT";
export const ACTION_LOAD_PARKING_DETECTION_REPORT = "LOAD_PARKING_DETECTION_REPORT";

/**
 * Класс для представления настроек аналитики детекции парковок.
 */
export class PerimeterSecuritySettings extends AnalyticSettings {
  get analyticType() {
    return ANALYTICS.parking_detection;
  }
}

export default {
  namespaced: true,
  state: {},
  mutations: {},
  actions: {
    /**
     * Загрузка списка настроек аналитик детекции парковок.
     *
     * @param {Object} context
     * @param {Array.<String>} camerasNumbers
     * @return {Promise.<Array.<PerimeterSecuritySettings>>}
     */
    async [ACTION_LOAD_PARKING_DETECTIONS_SETTINGS](context, {camerasNumbers}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/settings/", {camera_numbers: camerasNumbers});
        return response.data.results.map(rawPerimeterSecuritySettings => new PerimeterSecuritySettings(rawPerimeterSecuritySettings));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка настроек аналитики детекции парковок с конкретной камеры.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @return {Promise.<PerimeterSecuritySettings>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_SETTINGS]({dispatch}, {cameraNumber}) {
      const parkingDetectionsSettings = await dispatch(ACTION_LOAD_PARKING_DETECTIONS_SETTINGS, {camerasNumbers: [cameraNumber]});
      if (_.isEmpty(parkingDetectionsSettings)) {
        return null;
      }
      return parkingDetectionsSettings[0];
    },
    /**
     * Загрузка перечня событий по аналитике детекции парковок с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Array.<Number>} eventIds
     * @return {Promise.<Array.<PerimeterSecurityMessage>>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_EVENTS](context, {cameraNumber, eventIds}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/events/", {
          camera_number: cameraNumber,
          event_ids: eventIds,
        });
        return response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.parking_detection));
      } catch {
        return [];
      }
    },
    /**
     * Загрузка перечня событий по аналитике детекции парковок с камеры для формирования отчета.
     *
     * @param {Function} dispatch
     * @param {String} cameraNumber
     * @param {Number} eventId
     * @return {Promise.<PerimeterSecurityMessage>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_EVENT]({dispatch}, {cameraNumber, eventId}) {
      const parkingDetectionsSettings = await dispatch(ACTION_LOAD_PARKING_DETECTION_EVENTS, {cameraNumber, eventIds: [eventId]});
      if (_.isEmpty(parkingDetectionsSettings)) {
        return null;
      }
      return parkingDetectionsSettings[0];
    },
    /**
     * Загрузка перечня событий по определению детекции парковок с камеры для формирования отчета.
     *
     * @param {Object} context
     * @param {String} cameraNumber
     * @param {Date} archiveFrom
     * @param {Date} archiveTo
     * @param {String} query
     * @return {Promise.<Array.<PerimeterSecurityMessage>>}
     */
    async [ACTION_LOAD_PARKING_DETECTION_REPORT](context, {cameraNumber, archiveFrom, archiveTo, query = null, page, page_size}) {
      try {
        const response = await this.getters.ajaxClient.post("/v0/analytics/parking_detection/report/", {
          camera_number: cameraNumber,
          start: archiveFrom.toISOString(),
          end: archiveTo.toISOString(),
          query: query,
          page: page,
          page_size: page_size
        });
        const messages = response.data.results.map(rawEventData => makeMessage(rawEventData, cameraNumber, ANALYTICS.parking_detection));

        return {
          messages,
          response: response.data
        };
      } catch {
        return {
          messages: [],
          response: null
        };
      }
    },
  },
};
